"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button } from "../Button";
import { CloseIcon } from "../icons/CloseIcon";

type DrawerSize = "sm" | "md" | "lg";
/**
 * `flush` — the full-height slab against the viewport edge. The platform
 * default, and what every existing drawer uses.
 *
 * `inset` — a floating rounded card held 12px off the top, bottom and side,
 * with a border all the way round and a deeper shadow. This is the restaurant
 * artboards' drawer, and it reads as a panel laid ON the screen rather than as
 * a wall slid in from the edge. Opt in; it is not the default because ten
 * seller screens and one admin screen already draw the flush one.
 */
type DrawerVariant = "flush" | "inset";
type DrawerSide = "start" | "end";
type IconTone = "accent" | "muted";
type BodyTone = "surface" | "surface-alt";

const widthClass: Record<DrawerSize, string> = {
  sm: "w-full max-w-[420px]",
  md: "w-full max-w-[540px]",
  lg: "w-full max-w-[680px]",
};

/**
 * The same tiers, minus the 24px the inset frame takes out of the viewport.
 * A single `w-[min(...)]` rather than `w-full` + `max-w-*`, because the inset
 * panel needs BOTH bounds and two `max-w` utilities would just fight.
 */
const insetWidthClass: Record<DrawerSize, string> = {
  sm: "w-[min(420px,calc(100vw-1.5rem))]",
  md: "w-[min(540px,calc(100vw-1.5rem))]",
  lg: "w-[min(680px,calc(100vw-1.5rem))]",
};

export type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Drawer width tier — sm/md/lg. Default md (540px). */
  size?: DrawerSize;
  /** Frame: edge-to-edge slab (default) or floating inset card. */
  variant?: DrawerVariant;
  /** Slide-in side. Default "end" (right in LTR, left in RTL). */
  side?: DrawerSide;
  /**
   * Body background. Default "surface-alt" (sand) — drawer bodies render
   * white SectionCards on a sand backdrop so every drawer reads as the same
   * design. Pass "surface" if a specific drawer needs a flat white body.
   */
  bodyTone?: BodyTone;
  /** Small icon tile in the header (32×32). */
  icon?: ReactNode;
  /** "accent" (soft amber, default) or "muted" (sand). */
  iconTone?: IconTone;
  title?: ReactNode;
  subtitle?: ReactNode;
  closeLabel?: string;
  /** Slot before the icon (e.g. back button on multi-view drawers). */
  headerLeading?: ReactNode;
  /** Slot between title block and close X (e.g. status badge). */
  headerTrailing?: ReactNode;
  /** When provided, replaces the icon + title + subtitle stack entirely. */
  headerOverride?: ReactNode;
  /**
   * Accessible name for the drawer, for the one case the title cannot supply
   * it: a `headerOverride` (or a drawer with no `title`) leaves nothing on
   * screen to point `aria-labelledby` at. Ignored when a title renders — see
   * `Modal`'s note; the title node is pointed at directly so the name can
   * never drift from it.
   */
  ariaLabel?: string;
  /** Hint text on the left of the footer (12px muted). */
  footerLeft?: ReactNode;
  /** Action buttons on the right of the footer. */
  footerActions?: ReactNode;
  hideFooter?: boolean;
  /** Conditionally render the footer (e.g. sticky-when-dirty). */
  showFooterWhen?: boolean;
  children: ReactNode;
};

const CLOSE_DURATION_MS = 380;

/**
 * Side-sliding drawer with the same chrome language as `<Modal>` — icon tile
 * + title + subtitle in the header, scrollable body, optional footer with
 * `footerLeft` + `footerActions`. Handles backdrop click, Escape, focus trap,
 * and body scroll-lock just like `ModalSheet`. Use this instead of rolling
 * your own slide-in panel so every drawer in the app shares the same look.
 */
export function Drawer({
  isOpen,
  onClose,
  size = "md",
  variant = "flush",
  side = "end",
  bodyTone = "surface-alt",
  icon,
  iconTone = "accent",
  title,
  subtitle,
  closeLabel = "Close",
  headerLeading,
  headerTrailing,
  headerOverride,
  ariaLabel,
  footerLeft,
  footerActions,
  hideFooter = false,
  showFooterWhen,
  children,
}: DrawerProps) {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  /*
   * The drawer's accessible name — the twin of `Modal`'s. `role="dialog"` here
   * had no `aria-label` and no `aria-labelledby` either, so every drawer
   * announced as a bare "dialog". `titleNamesDrawer` mirrors the render
   * condition below: an `aria-labelledby` pointing at an absent id names the
   * drawer the empty string, which is worse than naming it nothing.
   *
   * The same DEFERRED note as `Modal.tsx` applies to promoting this title
   * `<span>` to an `<h2>` — it waits on the theme-parity gate, which cannot be
   * run here. See the comment beside `data-ui="dialog-title"` in `Modal.tsx`.
   */
  const titleId = useId();
  const titleNamesDrawer = !headerOverride && Boolean(title);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Two-effect mount/visibility sequence so the open animation always plays:
  //
  // Effect 1 — mount the panel as soon as isOpen flips true.
  // Effect 2 — once the panel is actually in the DOM (`isMounted` committed
  //            and the closed-state class painted), schedule the visibility
  //            transition on the next animation frame.
  //
  // Splitting the steps guarantees the browser paints the `translate-x-full`
  // (off-screen) state on one frame and `translate-x-0` (on-screen) on the
  // next, with the CSS transition interpolating between them. Doing both in
  // the same effect can collapse into a single paint frame under React 18's
  // automatic batching, which makes the slide-in animation get skipped.
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isMounted) return;
    if (isOpen) {
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setIsVisible(true));
        (closeTimerRef as unknown as { current: number }).current =
          id2 as unknown as number;
      });
      return () => cancelAnimationFrame(id1);
    }
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(
      () => setIsMounted(false),
      CLOSE_DURATION_MS,
    );
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, isMounted]);

  // Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Body scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    // Lock the REAL scroller, not just <body>. Shells that scroll inside an
    // inner element (the restaurant app's `.app-main`) mark it with
    // `data-app-scroll`; locking body alone left the page scrollbar live
    // behind the sheet — the contract's "scrollable backdrop" broken pattern.
    const scroller = document.querySelector<HTMLElement>("[data-app-scroll]");
    const prevScroller = scroller?.style.overflow ?? "";
    if (scroller) scroller.style.overflow = "hidden";
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      if (scroller) scroller.style.overflow = prevScroller;
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus trap — focus first interactive element on open, restore on close.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement;
    const id = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length > 0) focusable[0].focus();
    });
    return () => {
      cancelAnimationFrame(id);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen]);

  if (!isMounted) return null;

  const footerVisible =
    !hideFooter && (showFooterWhen === undefined || showFooterWhen);

  const iconTileClass =
    iconTone === "accent"
      ? "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[color-mix(in_oklab,var(--accent-500)_14%,white)] text-[color:var(--accent-600)]"
      : "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]";

  const isInset = variant === "inset";

  const sidePosition = isInset
    ? side === "end"
      ? "end-3"
      : "start-3"
    : side === "end"
      ? "end-0 border-s border-[color:var(--border-light)]"
      : "start-0 border-e border-[color:var(--border-light)]";

  /**
   * The inset panel starts 12px off its own edge, so sliding it by exactly its
   * own width would leave that 12px of card on screen through the whole
   * animation. It travels width + inset instead.
   */
  const offscreen = isInset
    ? side === "end"
      ? "rtl:-translate-x-[calc(100%+0.75rem)] translate-x-[calc(100%+0.75rem)]"
      : "rtl:translate-x-[calc(100%+0.75rem)] -translate-x-[calc(100%+0.75rem)]"
    : side === "end"
      ? "rtl:-translate-x-full translate-x-full"
      : "rtl:translate-x-full -translate-x-full";

  const transformClass = isVisible ? "translate-x-0" : offscreen;

  const frameClass = isInset
    ? [
        "top-3 bottom-3 rounded-[var(--radius-sheet,18px)] border-[length:var(--border-card-width,1px)] border-[color:var(--border-light)]",
        "overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.30)]",
        insetWidthClass[size],
      ].join(" ")
    : [
        "top-0 bottom-0 shadow-[var(--shadow-lg,0_24px_64px_rgba(11,18,32,0.4))]",
        widthClass[size],
      ].join(" ");

  /** The artboard's frame is tighter than the platform slab's 20px gutter. */
  const gutter = isInset ? "px-4" : "px-5";

  return (
    <>
      <div
        onClick={() => onCloseRef.current?.()}
        aria-hidden="true"
        data-ui="drawer-backdrop"
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity",
          isVisible
            ? "opacity-100 duration-[380ms]"
            : "opacity-0 duration-[280ms]",
        ].join(" ")}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleNamesDrawer ? titleId : undefined}
        aria-label={titleNamesDrawer ? undefined : ariaLabel}
        data-ui="drawer-panel"
        data-inset={isInset ? "" : undefined}
        className={[
          "fixed z-50 flex flex-col bg-[color:var(--surface)]",
          // Transition both `transform` and `translate` properties — Tailwind
          // v4's `translate-x-*` utilities use CSS `translate:` (not `transform:
          // translateX()`), so `transition-transform` alone wouldn't animate
          // the slide.
          "[transition-property:transform,translate] duration-[380ms] ease-[cubic-bezier(0.2,0.9,0.2,1)]",
          sidePosition,
          frameClass,
          transformClass,
        ].join(" ")}
      >
        <header
          data-ui="drawer-header"
          className={[
            "flex shrink-0 items-center gap-3 border-b border-[color:var(--border-light)] bg-[color:var(--surface)] py-4",
            gutter,
          ].join(" ")}
        >
          {headerLeading ? (
            <span className="shrink-0">{headerLeading}</span>
          ) : null}
          {headerOverride ? (
            <div className="flex min-w-0 flex-1 items-center">
              {headerOverride}
            </div>
          ) : (
            <>
              {icon ? <span data-ui="drawer-icon" className={iconTileClass}>{icon}</span> : null}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
                {title ? (
                  <span id={titleId} data-ui="drawer-title" className="truncate text-[15px] font-semibold text-[color:var(--text-primary)]">
                    {title}
                  </span>
                ) : null}
                {subtitle ? (
                  <span data-ui="drawer-subtitle" className="truncate text-[12px] text-[color:var(--text-muted)]">
                    {subtitle}
                  </span>
                ) : null}
              </div>
            </>
          )}
          {headerTrailing ? (
            <span className="shrink-0">{headerTrailing}</span>
          ) : null}
          <Button
            variant="link"
            size="sm"
            onClick={onClose}
            aria-label={closeLabel}
            leadingIcon={<CloseIcon size={18} />}
            data-ui="drawer-close"
          />
        </header>
        <div
          data-ui="drawer-body"
          className={[
            "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto py-5",
            gutter,
            bodyTone === "surface-alt"
              ? "bg-[color:var(--surface-alt)]"
              : "bg-[color:var(--surface)]",
          ].join(" ")}
        >
          {children}
        </div>
        {footerVisible ? (
          <footer
            data-ui="drawer-footer"
            className={[
              "flex shrink-0 items-center gap-3 border-t border-[color:var(--border-light)] bg-[color:var(--surface-alt)] pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]",
              gutter,
            ].join(" ")}
          >
            {footerLeft ? (
              <span className="min-w-0 text-[12px] text-[color:var(--text-muted)]">
                {footerLeft}
              </span>
            ) : null}
            {/* Inset drawers split the row between their actions, the way the
                artboards do — a cancel and a confirm sharing the full width
                rather than huddling at the end. `*:flex-1` reaches the caller's
                buttons without the caller having to know it is in a drawer. */}
            <div
              className={[
                "ms-auto flex items-center gap-2",
                // Equal halves. The artboard weights the pair 1 : 1.4, but
                // that needs an arbitrary child-variant Tailwind does not emit
                // here, and a 1.4x confirm is a nicety — the thing that was
                // actually wrong was the pair huddling at the end of the row.
                isInset && !footerLeft ? "w-full *:flex-1" : "",
              ].join(" ")}
            >
              {footerActions}
            </div>
          </footer>
        ) : null}
      </aside>
    </>
  );
}
